import React from 'react';
import { resolveStorageUrl } from '../utils/storageUrl';
import { getApplicationIconAsBlob } from '../api/client';

export interface AppIconImageProps {
  /** CDN storage path (örn. app-icons/{uuid}.png). */
  iconPath?: string;
  /** Legacy: ikon var mı / cache bust için; path yoksa API blob kullanılır. */
  appId?: string;
  iconUpdatedAt?: string;
  className?: string;
  alt?: string;
  fallback?: React.ReactNode;
}

/** Aynı app/icon için eşzamanlı legacy istekleri tek XHR’da birleştirir. */
const iconBlobInflight = new Map<string, Promise<Blob>>();

function fetchIconBlob(appId: string, version: number): Promise<Blob> {
  const key = `${appId}:${version}`;
  let pending = iconBlobInflight.get(key);
  if (!pending) {
    pending = getApplicationIconAsBlob(appId, version).finally(() => {
      iconBlobInflight.delete(key);
    });
    iconBlobInflight.set(key, pending);
  }
  return pending;
}

function cdnIconSrc(iconPath: string, iconUpdatedAt?: string): string {
  let url = resolveStorageUrl(iconPath);
  if (iconUpdatedAt) {
    const v = new Date(iconUpdatedAt).getTime();
    if (!Number.isNaN(v)) {
      url += (url.includes('?') ? '&' : '?') + `v=${v}`;
    }
  }
  return url;
}

/** Uygulama ikonunu CDN path veya (legacy) Authorization ile blob URL ile gösterir. */
const AppIconImage: React.FC<AppIconImageProps> = ({
  iconPath,
  appId,
  iconUpdatedAt,
  className,
  alt = 'App icon',
  fallback = null,
}) => {
  const [blobUrl, setBlobUrl] = React.useState<string | null>(null);
  const urlRef = React.useRef<string | null>(null);
  const hasPath = Boolean(iconPath?.trim());
  const legacyKey = !hasPath && appId && iconUpdatedAt ? `${appId}:${iconUpdatedAt}` : '';

  React.useEffect(() => {
    if (hasPath || !legacyKey || !appId || !iconUpdatedAt) {
      setBlobUrl(null);
      return;
    }
    let cancelled = false;
    setBlobUrl(null);
    fetchIconBlob(appId, new Date(iconUpdatedAt).getTime())
      .then(blob => {
        if (cancelled) return;
        const u = URL.createObjectURL(blob);
        urlRef.current = u;
        setBlobUrl(u);
      })
      .catch(() => {
        if (!cancelled) setBlobUrl(null);
      });
    return () => {
      cancelled = true;
      const u = urlRef.current;
      if (u) {
        URL.revokeObjectURL(u);
        urlRef.current = null;
      }
    };
  }, [hasPath, legacyKey, appId, iconUpdatedAt]);

  if (hasPath && iconPath) {
    return <img src={cdnIconSrc(iconPath, iconUpdatedAt)} alt={alt} className={className} />;
  }
  if (blobUrl) {
    return <img src={blobUrl} alt={alt} className={className} />;
  }
  if (fallback) {
    return <>{fallback}</>;
  }
  if (!hasPath && !legacyKey) {
    return null;
  }
  return (
    <div
      className={`bg-gray-200 dark:bg-gray-700 animate-pulse ${className ?? ''}`}
      aria-hidden
    />
  );
};

export default AppIconImage;
